/**
 * AdMesh Renderer
 * 
 * Handles rendering of recommendations in the specified container
 */

import ReactDOM from 'react-dom/client';
import type { AdMeshRecommendationResponse, AdMeshTheme, DelegationActivationPayload } from '../types/index';
import { AdMeshLayout } from '../components/AdMeshLayout';
import { AdMeshTracker } from './AdMeshTracker';
import { AdMeshProvider } from '../context/AdMeshProvider';
import { logger } from '../utils/logger';

export interface RenderOptions {
  containerId: string;
  followups_container_id?: string;
  response: AdMeshRecommendationResponse;
  theme?: AdMeshTheme;
  tracker: AdMeshTracker;
  sessionId: string;
  apiKey: string;
  apiBaseUrl?: string;
  language?: string;
  location?: {
    city?: string;
    state?: string;
    stateCode?: string;
    country?: string;
    countryCode?: string;
    zipcode?: string;
    latitude?: number;
    longitude?: number;
  };
  userId?: string;
  model?: string;
  messages?: Array<{ role: string; content: string; id?: string }>;
  onPasteToInput?: (content: string) => void;
  onDelegationConsent?: (recommendation: import('../types/index').PlatformResponse) => boolean | Promise<boolean>;
  onDelegationActivated?: (payload: DelegationActivationPayload) => void | Promise<void>;
  onStartDelegation?: (recommendation: import('../types/index').PlatformResponse) => void | Promise<void>;
  onExecuteQuery?: (query: string) => void | Promise<void>;
}

export class AdMeshRenderer {
  private roots: Map<string, ReactDOM.Root> = new Map();

  constructor() {
    // No configuration needed
  }

  /**
   * Render recommendations in the specified container
   */
  async render(options: RenderOptions): Promise<void> {
    try {
      logger.log('[AdMeshRenderer] 🎨 Attempting to render recommendations');

      // Check if we have any recommendations to render
      const recommendations = options.response.recommendations || [];
      if (recommendations.length === 0) {
        logger.log('[AdMeshRenderer] ⚠️ No recommendations to render - skipping render to avoid occupying space');
        return;
      }

      const container = document.getElementById(options.containerId);

      if (!container) {
        logger.error('[AdMeshRenderer] ❌ Container not found');
        throw new Error(`Container with ID "${options.containerId}" not found`);
      }

      logger.log('[AdMeshRenderer] ✅ Container ready');

      // Clean up existing root if any
      const existingRoot = this.roots.get(options.containerId);
      if (existingRoot) {
        logger.log('[AdMeshRenderer] 🧹 Cleaning up existing root');
        existingRoot.unmount();
        this.roots.delete(options.containerId);
      }

      // Clear the container's innerHTML to ensure React can create a fresh root
      container.innerHTML = '';

      // Create a new root
      const root = ReactDOM.createRoot(container);

      logger.log('[AdMeshRenderer] 📊 Rendering recommendations');

      // Get onPasteToInput from options or window object (for backward compatibility)
      // Wrap components in AdMeshProvider to provide context for hooks like useAdMesh()
      root.render(
        <AdMeshProvider
          apiKey={options.apiKey}
          sessionId={options.sessionId}
          theme={options.theme}
          apiBaseUrl={options.apiBaseUrl}
          language={options.language}
          location={options.location}
          userId={options.userId}
          model={options.model}
          messages={options.messages}
        >
          <AdMeshLayout
            recommendations={recommendations}
            theme={options.theme}
            sessionId={options.sessionId}
            onDelegationConsent={options.onDelegationConsent}
            onDelegationActivated={options.onDelegationActivated}
            onStartDelegation={options.onStartDelegation}
          />
        </AdMeshProvider>
      );

      // Show the container now that content is rendered (prevents empty space when no ads)
      container.style.display = 'block';

      logger.log('[AdMeshRenderer] ✅ Recommendations rendered successfully');

      // Store root for cleanup
      this.roots.set(options.containerId, root);

    } catch (error) {
      logger.error('[AdMeshRenderer] ❌ Error rendering recommendations');
      throw error;
    }
  }

  /**
   * Unmount a rendered component
   */
  unmount(containerId: string): void {
    const root = this.roots.get(containerId);
    if (root) {
      root.unmount();
      this.roots.delete(containerId);
      
      // Hide the container again to prevent empty space
      const container = document.getElementById(containerId);
      if (container) {
        container.style.display = 'none';
      }
    }
  }

  /**
   * Unmount all rendered components
   */
  unmountAll(): void {
    for (const [, root] of this.roots.entries()) {
      root.unmount();
    }
    this.roots.clear();
  }
}
